import React, { ReactNode, useState } from 'react';
import { BriefcaseIcon, ClipboardListIcon, LockIcon, PenLineIcon, UserRoundIcon } from 'lucide-react';
import { ChangeRequestModal } from '../../components/self/ChangeRequestModal';
import type { ChangeableField } from '../../components/self/ChangeRequestModal';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { PageHeader } from '../../components/ui/PageHeader';
import { selfEmergencyContact } from '../../data/selfService';
import { useSelfService } from '../../hooks/useSelfService';
import type { ChangeRequest } from '../../types/selfService';
import { displayName } from '../../utils/employees';
import { formatDate, tenure } from '../../utils/format';
import { getProfileDetails } from '../../utils/profile';
import { maskDob, maskSsn } from '../../utils/selfService';
import { isLicensed, teamFor } from '../../utils/teams';
export function MyInfo() {
  const {
    me,
    manager,
    changeRequests
  } = useSelfService();
  const [section, setSection] = useState<ChangeRequest['section'] | null>(null);
  const details = getProfileDetails(me);
  const a = details.address;
  const address = `${a.street1}${a.street2 ? `, ${a.street2}` : ''}, ${a.city}, ${a.state} ${a.zip}`;
  const legalName = [me.firstName, me.middleName, me.lastName].filter(Boolean).join(' ');
  const emergency = `${selfEmergencyContact.name} (${selfEmergencyContact.relationship}) · ${selfEmergencyContact.phone}`;
  const nmls = isLicensed(me) ? details.nmls : 'Not licensed';
  const fields: ChangeableField[] = [{
    section: 'Personal',
    label: 'Legal name',
    value: legalName
  }, {
    section: 'Personal',
    label: 'Preferred name',
    value: me.preferredName || me.firstName
  }, {
    section: 'Personal',
    label: 'Home address',
    value: address
  }, {
    section: 'Personal',
    label: 'Mobile phone',
    value: me.mobile
  }, {
    section: 'Personal',
    label: 'Personal email',
    value: details.homeEmail
  }, {
    section: 'Personal',
    label: 'Emergency contact',
    value: emergency
  }, {
    section: 'Personal',
    label: 'Date of birth',
    value: maskDob(me.birthDate)
  }, {
    section: 'Job',
    label: 'Job title',
    value: me.jobTitle
  }, {
    section: 'Job',
    label: 'Team',
    value: teamFor(me)
  }, {
    section: 'Job',
    label: 'Manager',
    value: manager ? displayName(manager) : '—'
  }, {
    section: 'Job',
    label: 'Work location',
    value: me.location
  }, {
    section: 'Job',
    label: 'NMLS #',
    value: nmls
  }];
  return <div data-id="element-1681" data-mp="1g4lkttkt2ysy">
      <PageHeader eyebrow="My info" title={displayName(me)} description="Your record is read-only. Spot something wrong? Send a change request and HR will update it." actions={<Button variant="primary" icon={PenLineIcon} onClick={() => setSection('Personal')} data-id="element-1683" data-mp="1p5thk77obrkx">
            Request a change
          </Button>} data-id="element-1682" data-mp="1g4vd5ntbpi1t" />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2" data-id="element-1684" data-mp="1u2a795uiue04">
        <Card title="Personal" icon={UserRoundIcon} action={<Button size="sm" onClick={() => setSection('Personal')} data-id="element-1686" data-mp="1z86vpjan1r46">
              Request a change
            </Button>} data-id="element-1685" data-mp="249cu9xqzc8lm">
          <dl className="divide-y divide-slate-100" data-id="element-1687" data-mp="1uc84lnxxdv02">
            <Row label="Legal name" data-id="element-1688" data-mp="1q9lje5vrf8bu">{legalName}</Row>
            <Row label="Preferred name" data-id="element-1689" data-mp="1qawms4lame2f">{me.preferredName || me.firstName}</Row>
            <Row label="Home address" data-id="element-1690" data-mp="2my16zwfb77mb">{address}</Row>
            <Row label="Mobile phone" data-id="element-1691" data-mp="2hzg75y3yypye">{me.mobile}</Row>
            <Row label="Personal email" data-id="element-1692" data-mp="2i1sxayjvs6mj">{details.homeEmail}</Row>
            <Row label="Emergency contact" data-id="element-1693" data-mp="2d7oqqgej20lz">{emergency}</Row>
            <Row label="SSN" data-id="element-1694" data-mp="297nztwa8rwy1">
              <Masked value={maskSsn(me.ssn)} data-id="element-1695" data-mp="296d0c8ntxbcq" />
            </Row>
            <Row label="Date of birth" data-id="element-1696" data-mp="35p26clkcd56e">
              <Masked value={maskDob(me.birthDate)} data-id="element-1697" data-mp="35q3sbl2wbc2w" />
            </Row>
          </dl>
        </Card>

        <Card title="Job" icon={BriefcaseIcon} action={<Button size="sm" onClick={() => setSection('Job')} data-id="element-1699" data-mp="2lfj3cru8eh15">
              Request a change
            </Button>} data-id="element-1698" data-mp="30xsj1rm9mdxo">
          <dl className="divide-y divide-slate-100" data-id="element-1700" data-mp="2qdklnaju2cbk">
            <Row label="Job title" data-id="element-1701" data-mp="2cgnv7a7mmwv7">{me.jobTitle}</Row>
            <Row label="Department" data-id="element-1702" data-mp="2ci8s36heyrw8">{me.department}</Row>
            <Row label="Team" data-id="element-1703" data-mp="2gi96ws8fd53t">{teamFor(me)}</Row>
            <Row label="Division" data-id="element-1704" data-mp="34726qzs63x3p">{me.division}</Row>
            <Row label="Manager" data-id="element-1705" data-mp="348d7sru8aqr4">
              {manager ? <span className="inline-flex items-center gap-2" data-id="element-1706" data-mp="396ojl8433bbz">
                  <Avatar name={displayName(manager)} seed={manager.id} size="xs" data-id="element-1707" data-mp="3989hn7plxcel" />
                  {displayName(manager)}
                  <span className="text-muted" data-id="element-1708" data-mp="2zg3pnxzcuuze">· {manager.jobTitle}</span>
                </span> : '—'}
            </Row>
            <Row label="Start date" data-id="element-1709" data-mp="3n3tlcvwrlo1f">
              {formatDate(me.hireDate)} <span className="text-muted" data-id="element-1710" data-mp="3rxoboi6vl19h">· {tenure(me.hireDate)}</span>
            </Row>
            <Row label="Employment status" data-id="element-1711" data-mp="1ggw2xoon1o3b">
              <span className="inline-flex items-center gap-2" data-id="element-1712" data-mp="1gfku3agc5q5c">
                <Badge tone="green" dot data-id="element-1713" data-mp="1bht4wo72si4u">
                  {me.status}
                </Badge>
                {me.employmentType}
              </span>
            </Row>
            <Row label="Work location" data-id="element-1714" data-mp="1l7uhojtzae4g">{me.location === 'Remote' ? 'Remote' : `${me.location} · On-site`}</Row>
            <Row label="NMLS #" data-id="element-1715" data-mp="xi06s7t7yzmu">{nmls === 'Not licensed' ? <span className="text-muted" data-id="element-1716" data-mp="xj1rl401f0hr">Not licensed</span> : nmls}</Row>
          </dl>
        </Card>
      </div>

      {changeRequests.length > 0 && <Card title="My change requests" subtitle="Sent to HR" icon={ClipboardListIcon} className="mt-6" bodyClassName="px-3 pb-3" data-id="element-1717" data-mp="17h97djilbci6">
          <ul className="divide-y divide-slate-100" data-id="element-1718" data-mp="12gbiyp6zlm88">
            {changeRequests.map(c => <li key={c.id} className="flex flex-col gap-1 px-3 py-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1719" data-mp="2iul5ddro50wf">
                <div className="min-w-0" data-id="element-1720" data-mp="2dz5sop5edb1l">
                  <p className="text-sm font-medium text-navy" data-id="element-1721" data-mp="2l1183gaxqnjw">
                    {c.field} <span className="font-normal text-muted" data-id="element-1722" data-mp="2pzch5oteou12">· {c.section}</span>
                  </p>
                  <p className="truncate text-xs text-muted" data-id="element-1723" data-mp="2uyp409ka213n">
                    “{c.current}” → <span className="text-slate-700" data-id="element-1724" data-mp="2uwl2fy9upe6j">“{c.requested}”</span>
                  </p>
                </div>
                <div className="flex items-center gap-3" data-id="element-1725" data-mp="229rv2x1yhgnk">
                  <span className="text-xs text-muted" data-id="element-1726" data-mp="22bbc4pvfrvqe">{formatDate(c.submitted, 'MMM d')}</span>
                  <Badge tone={c.status === 'completed' ? 'green' : 'orange'} dot data-id="element-1727" data-mp="279cvlbwrxx2e">
                    {c.status === 'completed' ? 'Updated by HR' : 'With HR'}
                  </Badge>
                </div>
              </li>)}
          </ul>
        </Card>}

      <ChangeRequestModal open={section !== null} onClose={() => setSection(null)} employeeId={me.id} fields={fields} section={section ?? 'Personal'} data-id="element-1728" data-mp="1ji7iv5m4rkwb" />
    </div>;
}
function Row({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) {
  return <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[160px_minmax(0,1fr)] sm:gap-4" data-id="element-1729" data-mp="5kwdynn6hfdo">
      <dt className="text-sm text-muted" data-id="element-1730" data-mp="1jt36fjzm9jb">{label}</dt>
      <dd className="text-sm font-medium text-navy" data-id="element-1731" data-mp="3sqvpotovrrn7">{children}</dd>
    </div>;
}
function Masked({
  value
}: {
  value: string;
}) {
  return <span className="inline-flex flex-wrap items-center gap-2" data-id="element-1732" data-mp="3nv82ukxvfakv">
      <span className="font-mono tracking-wider" data-id="element-1733" data-mp="3iukbfy5qcrq4">{value}</span>
      <span className="inline-flex items-center gap-1 text-[11px] font-normal text-muted" data-id="element-1734" data-mp="3e2iuw01j4eua">
        <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-1735" data-mp="3a17caeh2otvl" /> Hidden for your security
      </span>
    </span>;
}